import { useCallback, useEffect, useId, useRef, useState, type CSSProperties } from 'react';

import { EXAMS } from '../../data/mock/exams';
import { beispielIds } from '../../data/mock/toBundle';
import { fuehrtSchauspieler } from '../../domain/schauspieler';
import { lageAus, mergeCatalogue, type ExamListing, type ExamRef } from '../../domain/examCatalogue';
import { trefferAusCode } from '../../domain/qrAuswahl';
import { lisQrNutzlast } from '../../domain/qrNutzlast';
import type { Person } from '../../domain/types';
import { useLocaleStore, useT } from '../../i18n';
import { KATALOG, type Schluessel } from '../../platform/einstellungen';
import { eintragsText, wertText } from './einstellungsFelder';
import { listExams } from '../../persistence/readExam';
import { getStore } from '../../persistence/store';
import { appendAppLog } from '../../platform/appLog';
import { geraeteName } from '../../platform/appVersion';
import { kannBeenden } from '../../platform/beenden';
import { Signing } from '../../platform/signing';
import { ladeClassicExam } from '../../server/classic/download';
import { ServerFehler } from '../../server/client';
import { bildeAblage, pruefePasswort } from '../../domain/geraetePasswort';
import { leseGeraetePasswort, setzeGeraetePasswort } from '../../persistence/importExam';
import { PruefungsPasswortDialog } from './PruefungsPasswortDialog';
import { ladeRelayExam, relayMedienHoler } from '../../server/download';
import { uebernimmAenderung, veraltete } from '../../server/nachladen';
import { flushNow } from '../../persistence/autosave';
import type { ServerClient } from '../../server/client';
import { medienHoler } from '../../server/classic/medien';
import {
  aktuellerClient, baueClient, holeKatalogFuer, ladeZugang, type Servermodus,
} from '../../server/zugang';
import { startPreview } from '../../preview/startPreview';
import { mischeEinstellungenNeu } from '../../store/einstellungenMischen';
import { selectStationOrNull, useAppStore, warteAufExam } from '../../store/useAppStore';
import { Icon, type IconName } from '../../ui/Icon';
import { Sheet, SheetAction } from '../../ui/Sheet';
import { Switch } from '../../ui/Switch';
import { schreibeQrUeberschreibungen } from '../../persistence/qrUeberschreibungen';
import { scanCandidateCode } from '../../platform/scanner';
import { ausClassicEinstellungen } from '../../server/classic/einstellungenQr';
import { ausRelayEinstellungen, type QrVorschau } from '../../server/relayEinstellungenQr';
import { meldeUnbekannte } from '../../platform/unbekannteEinstellungen';
import { useToastStore } from '../../ui/Toast';
import { DeleteExamDialog } from './DeleteExamDialog';
import { QrUebernahmeDialog } from './QrUebernahmeDialog';
import { LOESCH_FRIST_MS, tippen, type LoeschScharf } from './loeschScharf';
import styles from './SetupSheet.module.css';

/**
 * Der Prüfungskatalog: was auf dem Gerät liegt, was der Server anbietet, und was nur noch
 * hier steht.
 *
 * Die Serverliste ist `null`, solange niemand nachgefragt hat — das ist ausdrücklich etwas
 * anderes als eine leere Antwort (s. `mergeCatalogue`). Ohne Nachfrage trägt keine Prüfung
 * eine Warnung.
 */
/**
 * Übernimmt, was Relay an Prüfungen dieses Geräts geändert hat (Regel des Nutzers, 2026-10-10;
 * `server/nachladen.ts`). Läuft mit dem Takt der Auswahl. Jede Fassung wird **einmal** versucht:
 * ein Fehlschlag schriebe sonst alle zehn Sekunden dieselbe Zeile ins Log; die Auswahl warnt
 * dann weiter. Liefert, ob sich am Gerät etwas geändert hat.
 */
async function gleicheAenderungenAb(
  client: ServerClient,
  url: string,
  katalog: readonly ExamRef[],
  versucht: Set<string>,
): Promise<boolean> {
  const store = getStore();
  const offen = (await veraltete(store, katalog)).filter((e) => !versucht.has(e.id + '@' + e.stand));
  if (offen.length === 0) return false;
  // Erst sichern: eine gerade begonnene Bewertung muss als Zeile stehen, sonst gälte die
  // Prüfung als nicht gestartet und würde ersetzt.
  await flushNow();
  let geaendert = false;
  // Angemeldet wird erst, wenn wirklich eine Datei zu holen ist — und dann einmal.
  let token: Promise<string> | undefined;
  const hole = async (id: string) =>
    relayMedienHoler(url, await (token ??= client.anmelden().then((a) => a.token)))(id);
  for (const ref of offen) {
    versucht.add(ref.id + '@' + ref.stand);
    try {
      const was = await uebernimmAenderung(client, store, ref, hole);
      if (was === 'warnen') continue;
      geaendert = true;
      await appendAppLog(
        'Prüfung ' + ref.id + ' von Relay geändert: ' + (was === 'neu' ? 'neu geladen.' : 'nur das Ende übernommen (gestartet).'),
      );
      if (ref.id === useAppStore.getState().examId) {
        await useAppStore.getState().ladeInhaltNeu(ref.id);
        await mischeEinstellungenNeu();
      }
    } catch (f) {
      await appendAppLog('Änderung an Prüfung ' + ref.id + ' nicht übernommen: ' + String(f), 'warn');
    }
  }
  return geaendert;
}

/**
 * Wie oft nachgesehen wird, solange die Auswahl offen ist.
 *
 * Zehn Sekunden ist derselbe Takt, in dem der Classic-Modus ohnehin sendet (`classic/takt.ts`).
 * ⚠️ Gefragt wird **nur bei offener Auswahl**: ein Hintergrundabruf während der Prüfung wäre
 * Verkehr, den niemand liest — der Prüfer schaut auf den Bogen, nicht auf den Katalog.
 */
const KATALOG_TAKT_MS = 10_000;

function useExamCatalogue() {
  const [lokal, setLokal] = useState<ExamRef[]>([]);
  const [server, setServer] = useState<ExamRef[] | null>(null);
  const [läuft, setLäuft] = useState(false);
  /** Dasselbe wie `läuft`, aber sofort lesbar — der Zeitgeber wartet nicht auf React. */
  const läuftRef = useRef(false);
  /**
   * Was schiefging — und **wobei**. Ohne die Art stünde über einem gescheiterten Download
   * „Aktualisieren fehlgeschlagen", und der Prüfer suchte den Fehler an der falschen Stelle.
   */
  const [fehler, setFehler] = useState<
    { art: 'refresh' | 'download'; text: string } | { art: 'keinServer' } | null
  >(null);
  /**
   * Ob auch Prüfungen ausserhalb des Zeitfensters gezeigt werden.
   *
   * ⚠️ **Ohne das sind die meisten Prüfungen unerreichbar.** Der Classic-Server filtert bei
   * `current=true` auf `endDate >= heute` **und** `startDate <= heute + 14 Tage`; am Server des
   * Nutzers kamen so am 2026-09-11 **2 von 19** Prüfungen durch. Der alte Dialog hat dafür einen
   * Haken, unserer hatte keinen — eine abgelaufene oder weit entfernte Prüfung liess sich
   * schlicht nicht herunterladen.
   *
   * Relay kennt den Filter nicht; dort ändert der Haken nichts und wird deshalb auch nicht
   * angezeigt.
   */
  const [alleAnzeigen, setAlleAnzeigen] = useState(false);
  /** Nur zum Anzeigen des Hakens — die Weiche selbst steht in `zugang.ts`. */
  const [modus, setModus] = useState<Servermodus>('relay');

  const lokalLesen = useCallback(async () => {
    setLokal(await listExams(getStore()));
  }, []);
  /** Was `gleicheAenderungenAb` schon versucht hat, als `id@stand` — einmal je Fassung. */
  const versuchtRef = useRef(new Set<string>());

  useEffect(() => {
    // Mit Fang: scheitert das Lesen der Ablage, bliebe die Liste sonst wortlos leer, und der
    // Prüfer hielte eine geladene Prüfung für verschwunden.
    void lokalLesen().catch((f: unknown) => setFehler({ art: 'refresh', text: String(f) }));

    // ⚠️ Der Modus wird **beim Öffnen** geholt, nicht erst beim Aktualisieren. Sonst erschiene
    // der Haken „Alle Prüfungen anzeigen" erst nach dem ersten Abruf — also genau dann nicht,
    // wenn er gebraucht wird: beim ersten Blick auf eine zu kurze Liste. Am Simulator gesehen.
    void (async () => {
      try {
        const { modus: gilt } = await ladeZugang(getStore(), useAppStore.getState().clientId);
        setModus(gilt);
      } catch {
        // Kein Zugang lesbar heisst „kein Server" — dann bleibt es bei Relay, und der Haken
        // bleibt verborgen. Das ist keine Meldung wert.
      }
    })();
  }, [lokalLesen]);

  /**
   * `alleZeigen` überschreibt den Zustand für **diesen** Abruf.
   *
   * Nötig, weil der Haken sofort nachladen soll: `setAlleAnzeigen` wirkt erst im nächsten
   * Durchlauf, und ein `aktualisieren()` direkt danach fragte noch mit dem alten Wert. Ohne den
   * Parameter müsste der Prüfer nach dem Haken ein zweites Mal auf „Aktualisieren" tippen —
   * am Simulator gesehen, und es sah aus, als täte der Haken nichts.
   */
  const aktualisieren = useCallback(async (alleZeigen?: boolean) => {
    läuftRef.current = true;
    setLäuft(true);
    /*
     * ⚠️ **Die Meldung wird erst bei Erfolg genommen, nicht schon beim Anlauf.**
     *
     * Vorher stand hier `setFehler(null)`: eine stehende Meldung verschwand mit jedem Schlag
     * des Taktes und kam eine Wimpernschlag später zurück — alle zehn Sekunden, und weil der
     * Kasten über der Liste sitzt, rückte die ganze Liste mit. Das war die zweite Quelle des
     * Flackerns, das der Nutzer am 2026-09-14 gemeldet hat (die erste war die mitwachsende
     * Höhe der Liste, s. `SetupSheet.module.css`).
     *
     * Ein Fehlschlag, der noch gilt, soll auch sichtbar bleiben, solange er gilt.
     */
    try {
      const identity = await Signing.getIdentity().catch(() => null);
      const client = await aktuellerClient(getStore(), useAppStore.getState().clientId, {
        deviceIdentifier: identity?.deviceId ?? '',
        deviceName: geraeteName() || (identity?.deviceName ?? ''),
      });
      if (!client) {
        /*
         * Kein Server eingerichtet ist kein Fehlschlag, aber auch kein Ergebnis: die
         * Serverliste bleibt `null` (= „nicht nachgesehen"), damit keine lokale Prüfung
         * plötzlich als verwaist gilt. Gesagt wird es trotzdem.
         *
         * ⭐ **Als eigene Art, und ohne Text.** Bis zum 2026-09-23 stand hier die fertige
         * Zeile aus `t`, und zwar in **zwei** Hinsichten falsch: sie erschien unter der roten
         * Überschrift „Der Server ist nicht erreichbar" — ein Gerät ohne Server ist aber
         * nicht unerreichbar, sondern allein, und das ist der Normalbetrieb
         * (`keine-fehler-im-normalbetrieb`). Und der Text blieb in der Sprache stehen, die
         * beim ersten Lauf galt: im Fenster stand deutsche Überschrift über englischem Satz.
         * Jetzt merkt sich der Zustand nur die **Art**; die Worte holt die Anzeige aus `t`.
         */
        await lokalLesen();
        setFehler({ art: 'keinServer' });
        return;
      }
      const zugang = await ladeZugang(getStore(), useAppStore.getState().clientId);
      const gilt = zugang.modus;
      setModus(gilt);
      const [katalog] = await Promise.all([
        holeKatalogFuer(gilt, client, alleZeigen ?? alleAnzeigen),
        lokalLesen(),
      ]);
      setServer(katalog);
      if (gilt === 'relay' && (await gleicheAenderungenAb(client, zugang.url, katalog, versuchtRef.current))) {
        await lokalLesen();
      }
      // Erst jetzt: der Lauf ist durch, eine alte Meldung ist damit überholt.
      setFehler(null);
    } catch (e) {
      // Die Serverliste bleibt, wie sie war: eine gescheiterte Nachfrage darf nicht dazu
      // führen, dass plötzlich alles als verwaist gilt.
      setFehler({ art: 'refresh', text: e instanceof Error ? e.message : String(e) });
    } finally {
      läuftRef.current = false;
      setLäuft(false);
    }
  }, [alleAnzeigen, lokalLesen]);

  /**
   * Der Takt: sofort beim Öffnen und danach alle zehn Sekunden.
   *
   * ⚠️ **Ohne die Wache liefen die Abrufe übereinander.** Auf einem trägen Netz dauert ein
   * Durchlauf länger als zehn Sekunden; der nächste Schlag stiege dann in einen laufenden
   * hinein. `läuftRef` statt `läuft`: der Zustand käme erst im nächsten Durchlauf an, der
   * Zeitgeber schlägt aber vorher.
   */
  useEffect(() => {
    const schlag = (): void => {
      if (läuftRef.current) return;
      void aktualisieren();
    };
    schlag();
    const id = window.setInterval(schlag, KATALOG_TAKT_MS);
    return () => window.clearInterval(id);
  }, [aktualisieren]);

  const liste = mergeCatalogue(lokal, server, BEISPIELE);

  // Was die Kopfzeile zeigt. Nur Zahlen — welche Prüfung es trifft, steht hier in der Liste.
  const setzeKatalogLage = useAppStore((z) => z.setzeKatalogLage);
  const lage = lageAus(liste, fehler?.art === 'refresh');
  useEffect(() => {
    setzeKatalogLage({ netzFehler: lage.netzFehler, veraltet: lage.veraltet, verwaist: lage.verwaist });
  }, [setzeKatalogLage, lage.netzFehler, lage.veraltet, lage.verwaist]);

  return {
    liste,
    läuft,
    fehler,
    aktualisieren,
    lokalLesen,
    alleAnzeigen,
    setAlleAnzeigen,
    modus,
    /** Damit auch das Herunterladen einer einzelnen Prüfung seinen Fehlschlag zeigen kann. */
    melde: setFehler,
  };
}

/**
 * Prüfung einrichten: welche Prüfung, welche Station, wer prüft, wer spielt.
 *
 * Jede Wahl greift sofort — „Übernehmen" schließt nur. Ein Entwurfszustand mit Verwerfen
 * wäre hier mehr Verwaltung als Nutzen: die Auswahl ist jederzeit erneut änderbar, und im
 * Prüfungsbetrieb soll ein Fehlgriff mit einem zweiten Tippen behoben sein.
 */
/**
 * Der QR-Weg der Prüfungsauswahl: scannen, zeigen, übernehmen.
 *
 * **Zwei Nutzlasten, ein Knopf.** `settings-…` richtet das Gerät ein (das Format des
 * Classic-Servers, s. `domain/qrNutzlast.ts`), `auswahl-…` setzt Prüfung, Station, Prüfer und
 * Schauspieler. Was es war, entscheidet der Leser, nicht die Oberfläche.
 *
 * ⚠️ **Einstellungen werden erst gezeigt, dann gesetzt.** Ein Code des Classic-Servers trägt den
 * Serverzugang im Klartext und schlägt auf Wunsch des Nutzers sogar die Geräteverwaltung —
 * beides gehört vor Augen, ehe es gilt.
 *
 * ⚠️ **Die Auswahl gilt sofort.** Dort ist nichts unwiederbringlich: sie lässt sich mit zwei
 * Tippern von Hand ändern, und eine Rückfrage, die nichts zu warnen hat, erzieht dazu,
 * Rückfragen wegzutippen (dieselbe Überlegung wie im `DeleteExamDialog`).
 */
function useQrEinlesen() {
  const t = useT();
  const melde = useToastStore((z) => z.zeigeText);
  const selectExam = useAppStore((z) => z.selectExam);
  const selectStation = useAppStore((z) => z.selectStation);
  const selectExaminer = useAppStore((z) => z.selectExaminer);
  const selectActor = useAppStore((z) => z.selectActor);
  const [vorschau, setVorschauRoh] = useState<QrVorschau | null>(null);
  /**
   * Ob zur gezeigten Vorschau die Logzeile über unbekannte Namen schon steht. Der Dialog ruft
   * nach „Übernehmen" auch `onClose` — ohne diese Marke stünde danach noch „verworfen" im Log.
   */
  const unbekannteGemeldet = useRef(false);
  const setVorschau = (v: QrVorschau | null) => {
    unbekannteGemeldet.current = false;
    setVorschauRoh(v);
  };
  const meldeVorschau = (v: QrVorschau, ausgang: 'übernommen' | 'verworfen') => {
    if (unbekannteGemeldet.current) return;
    unbekannteGemeldet.current = true;
    meldeUnbekannte(v.herkunft === 'relay' ? 'Relay-QR' : 'Classic-QR', v.unbekannt, ausgang);
  };

  const scannen = useCallback(async () => {
    const roh = await scanCandidateCode(t.qr.scanHinweis).catch(() => null);
    if (!roh) return;
    const gelesen = lisQrNutzlast(roh);

    if (gelesen.art === 'unbekannt') {
      melde(
        gelesen.grund === 'fremd'
          ? t.qr.fremd
          : gelesen.grund === 'fassung'
            ? t.qr.fassung
            : gelesen.grund === 'geraeteart'
              ? t.qr.geraeteart
              : t.qr.kaputt,
      );
      return;
    }

    // Relays Preview: abholen und gleich hinein in den Bogen (`preview/startPreview.ts`).
    if (gelesen.art === 'vorschau') {
      await startPreview({ url: gelesen.url, id: gelesen.id });
      return;
    }

    // Der Hauptweg (Entscheidung des Nutzers, 2026-10-08): Relays Einstellungscode.
    if (gelesen.art === 'relayEinstellungen') {
      setVorschau(ausRelayEinstellungen(gelesen.werte, gelesen.gruppe));
      return;
    }

    if (gelesen.art === 'einstellungen') {
      setVorschau(ausClassicEinstellungen(gelesen.werte));
      return;
    }

    // ⚠️ **Nur setzen, was dieses iPad wirklich hat.** Ein Code kann auf eine Prüfung zeigen,
    // die hier nie geladen wurde; `selectExam` würde sie dann leer stehen lassen. Lieber ein
    // klarer Satz als eine halbe Auswahl.
    const lokal = await listExams(getStore()).catch(() => []);
    if (!lokal.some((e) => e.id === gelesen.exam)) {
      melde(t.qr.examFehlt);
      return;
    }
    selectExam(gelesen.exam);

    // Station, Prüfer und Schauspieler erst, wenn die Prüfung im Store steht — `selectExam`
    // lädt sie nach.
    const gefunden = await warteAufExam(gelesen.exam);
    if (!gefunden) {
      melde(t.qr.examFehlt);
      return;
    }
    const treffer = trefferAusCode(gelesen, gefunden);
    // ⚠️ **Nicht abgewartet, und das ist gewollt.** `selectStation` zieht seit dem
    // 2026-09-28 erst den Bogen der verlassenen Station ab; darauf zu warten hielte die
    // übrige Wahl aus dem Code unnötig auf. Schliesst dieser Zweig gleich darauf das Blatt,
    // bleibt es zu — `selectStation` schlägt nur sein eigenes Blatt wieder auf.
    if (treffer.station) void selectStation(treffer.station);
    if (treffer.examiner) selectExaminer(treffer.examiner);
    if (treffer.actor) selectActor(treffer.actor);

    if (treffer.fehlt) {
      melde(t.qr.stationFehlt);
      return;
    }

    /*
     * ⚠️ **Trägt der Code die ganze Wahl, wird nicht noch einmal gefragt** (Vorgabe des
     * Nutzers, 2026-09-14): Prüfung, Station und Prüfer stehen, der Schauspieler soweit
     * genannt — dann ist das Blatt beantwortet, und ein Prüfer, der gerade seinen Code
     * gescannt hat, soll nicht denselben Knopf noch einmal drücken müssen.
     *
     * Wohin es geht, hängt daran, woher das Blatt kommt: im Startablauf weiter zur
     * Prüflingsauswahl (`confirmSetup` — dieselbe Schwelle wie der Bestätigen-Knopf), später
     * aus der Kopfzeile geöffnet einfach zurück zum Bogen.
     */
    if (treffer.starten) {
      const z = useAppStore.getState();
      if (z.startup.kind === 'auswahl') z.confirmSetup();
      else z.closeSheet();
      return;
    }

    melde(t.qr.auswahlGesetzt);
  }, [melde, selectExam, selectStation, selectExaminer, selectActor, t]);

  const uebernehmen = useCallback(async () => {
    if (!vorschau) return;
    meldeVorschau(vorschau, 'übernommen');
    const store = getStore();
    const clientId = useAppStore.getState().clientId;
    await schreibeQrUeberschreibungen(store, clientId, vorschau.uebernommen);

    // Sofort gültig machen, ohne Neustart: dieselbe Mischung wie beim Start (`main.tsx`).
    await mischeEinstellungenNeu();
    melde(t.qr.gesetzt);
  }, [vorschau, melde, t]);

  const verwerfen = () => {
    if (vorschau) meldeVorschau(vorschau, 'verworfen');
    setVorschauRoh(null);
  };

  return { vorschau, scannen, uebernehmen, verwerfen };
}



/** Die Beispielprüfungen stehen auf keinem Server — sie gelten nie als verwaist. */
const BEISPIELE = beispielIds(EXAMS);

export function SetupSheet() {
  const t = useT();
  // Verknüpft die Beschriftung in der Kopfzeile mit dem Schalter daneben.
  const alleId = useId();
  const katalog = useExamCatalogue();
  const [löschZiel, setLöschZiel] = useState<ExamListing | null>(null);

  /**
   * Der scharfgeschaltete Papierkorb — erster Tipp schaltet scharf, zweiter löscht.
   *
   * Die Entscheidung selbst steht in `loeschScharf.ts` und ist dort geprüft; hier liegt nur,
   * was sich nicht rechnen lässt: der Zustand und eine Uhr, die den Knopf zurückfallen lässt,
   * wenn der zweite Tipp ausbleibt.
   */
  const [scharf, setScharf] = useState<LoeschScharf>(null);
  /** Welche Prüfung ihre Einstellungen gerade aufgeklappt zeigt. */
  const [einstellungenVon, setEinstellungenVon] = useState<string | null>(null);
  const locale = useLocaleStore((s) => s.locale);
  /** Ein Wert so, wie ihn der Einstellungsdialog nennt — „an“ statt `true`. */
  const zeigeEinstellung = (name: string, roh: string): string =>
    typeof KATALOG[name as Schluessel]?.vorgabe === 'boolean'
      ? roh === 'true'
        ? t.setup.einstellungAn
        : roh === 'false'
          ? t.setup.einstellungAus
          : roh
      : wertText(name, roh, locale);
  const scharfUhr = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Wird das Blatt geschlossen, während ein Papierkorb scharf ist, liefe die Uhr ins Leere
  // und setzte den Zustand einer abgebauten Anzeige.
  useEffect(
    () => () => {
      if (scharfUhr.current !== null) clearTimeout(scharfUhr.current);
    },
    [],
  );

  const papierkorbTippen = (ziel: ExamListing) => {
    const folge = tippen(scharf, ziel.id, Date.now());
    if (scharfUhr.current !== null) clearTimeout(scharfUhr.current);
    scharfUhr.current = null;

    if (folge.aktion === 'loeschen') {
      setScharf(null);
      // Hat die Prüfung ein Passwort, zuerst danach fragen (Vorgabe des Nutzers, 2026-10-09).
      // Ein Löschbefehl der Verwaltung (`WIPE_DATA`) geht nicht hier durch und braucht keins.
      void leseGeraetePasswort(getStore(), ziel.id).then((ablage) => {
        if (ablage) setLöschSperre({ ziel, ablage });
        else setLöschZiel(ziel);
      });
      return;
    }

    setScharf(folge.scharf);
    // Etwas mehr als die Frist: der Balken soll sichtbar auslaufen, ehe die Fläche
    // zurückspringt. Fiele beides zusammen, sähe es aus, als sei der Knopf zu früh erloschen.
    scharfUhr.current = setTimeout(() => {
      scharfUhr.current = null;
      setScharf(null);
    }, LOESCH_FRIST_MS + 80);
  };

  /**
   * Eine Prüfung holen — zum ersten Mal oder **erneut**.
   *
   * ⚠️ Erneut laden ist kein Luxus: ohne das käme eine auf dem Server berichtigte Prüfung nie
   * aufs iPad. Der einzige andere Weg wäre Löschen und neu holen, und `deleteExam` nimmt die
   * erfassten Bewertungen mit. `importExam` dagegen **ersetzt nur den Inhalt** und lässt
   * `ExamLog` und `CandidateExamMetadata` stehen — genau dafür ist es gebaut.
   */
  /**
   * Holt eine Prüfung. `passwort`: das Prüfungspasswort, falls sie eins hat (Vorgabe des
   * Nutzers, 2026-10-09) — Relay bekommt es als `X-Exam-Password`, bei Classic wird daraus die
   * Ablageform fürs Löschen gebildet. Liefert `false`, wenn Relay das Passwort ablehnt; der
   * Dialog fragt dann erneut.
   */
  const herunterladen = useCallback(
    (examId: string, passwort?: string): Promise<boolean> => {
      setBeschäftigt(examId);
      return (async () => {
        try {
          katalog.melde(null);
          const store = getStore();
          const clientId = useAppStore.getState().clientId;
          const zugang = await ladeZugang(store, clientId);

          const identity = await Signing.getIdentity().catch(() => null);
          const client = baueClient(zugang, {
            deviceIdentifier: identity?.deviceId ?? '',
            deviceName: geraeteName() || (identity?.deviceName ?? ''),
          });

          // Die Anhänge der Stationen kommen auf **beiden** Gleisen gleich mit: danach ist die
          // Prüfung vollständig offline (Entscheidung des Nutzers, 2026-09-13). Ein Anhang, der
          // scheitert, hält den Download nicht auf.
          if (zugang.modus === 'classic') {
            // `GET /rest/osces/{id}` liefert Stationen, Aufgaben, Antworten, Personen und
            // Bausteine in einem Zug (`docs/protokoll-classic.md` §3).
            await ladeClassicExam(
              client,
              store,
              examId,
              new Date(),
              medienHoler({ baseUrl: zugang.url, login: zugang.login, password: zugang.password }),
            );
            if (passwort) await setzeGeraetePasswort(store, examId, await bildeAblage(passwort));
            await appendAppLog('Prüfung ' + examId + ' vom Classic-Server geladen.');
          } else {
            // ⬅️ **Seit dem 2026-09-16.** Vorher stand hier ein klarer Satz, dass Relay den
            // Inhalt einer Prüfung nicht liefern kann — `/exam/get` gab nur die Prüfungszeile.
            // Mit `/exam/document` gibt es den Baum (`server/download.ts`).
            //
            // ⚠️ Das Token für die Mediendateien kommt aus der **laufenden** Anmeldung: Relay
            // meldet sich mit Bearer an, nicht mit HTTP Basic wie Classic.
            const anmeldung = await client.anmelden();
            const ergebnis = await ladeRelayExam(
              client,
              store,
              examId,
              relayMedienHoler(zugang.url, anmeldung.token),
              passwort,
            );
            await appendAppLog('Prüfung ' + examId + ' von Relay geladen.');
            if (ergebnis.ohnePunkte) {
              // ⚠️ **Kein stiller Fehler.** Ohne `scalaValues` und Punkte je Antwortzeile käme
              // jede Aufgabe als Checkliste mit lauter Einspunktzeilen an; der Bogen sähe
              // normal aus und die Summe wäre falsch (`docs/auftrag-relay-antwortfelder.md`).
              katalog.melde({ art: 'download', text: t.server.downloadOhnePunkte });
              await appendAppLog('Prüfung ' + examId + ' kam ohne Punktzahlen an.', 'warn');
            }
          }
          await katalog.lokalLesen();
          // War es die ausgewählte Prüfung, gelten ihre neuen Einstellungen sofort.
          await mischeEinstellungenNeu().catch((fehler: unknown) =>
            appendAppLog('Einstellungen nach dem Herunterladen gescheitert: ' + String(fehler), 'warn'),
          );
          return true;
        } catch (f) {
          // Relay lehnt das Passwort ab: kein Fehler fürs Protokoll, der Dialog fragt erneut.
          if (f instanceof ServerFehler && (f.code === 'EXAM_PASSWORD_WRONG' || f.code === 'EXAM_PASSWORD_REQUIRED')) {
            await appendAppLog('Prüfungspasswort für ' + examId + ' abgelehnt (' + f.code + ').', 'warn');
            return false;
          }
          // Ohne Fang endete ein Fehlschlag als unbehandelte Ablehnung: die Wolke hörte auf zu
          // arbeiten, die Prüfung war nicht da, und nichts sagte warum. Das Herunterladen ist
          // der einzige Weg, eine Prüfung auf das iPad zu bekommen — es muss sich melden.
          katalog.melde({ art: 'download', text: f instanceof Error ? f.message : String(f) });
          await appendAppLog('Herunterladen von ' + examId + ' gescheitert: ' + String(f), 'error');
          return true;
        } finally {
          setBeschäftigt(null);
        }
      })();
    },
    [katalog, t],
  );
  const [beschäftigt, setBeschäftigt] = useState<string | null>(null);
  const [passwortZiel, setPasswortZiel] = useState<ExamListing | null>(null);
  const [löschSperre, setLöschSperre] = useState<{ ziel: ExamListing; ablage: string } | null>(null);
  const station = useAppStore(selectStationOrNull);
  const closeSheet = useAppStore((s) => s.closeSheet);
  const openSheet = useAppStore((s) => s.openSheet);
  const setBeendenOffen = useAppStore((s) => s.setBeendenOffen);
  const startup = useAppStore((s) => s.startup);
  const confirmSetup = useAppStore((s) => s.confirmSetup);
  const forgetExam = useAppStore((s) => s.forgetExam);

  const examId = useAppStore((s) => s.examId);
  const examinerId = useAppStore((s) => s.examinerId);
  const actorId = useAppStore((s) => s.actorId);

  // ⚠️ **Die gewählte Prüfung, nicht die geladene.** Der Store hält den Inhalt der zuletzt
  // gelesenen Prüfung auch dann, wenn niemand sie gewählt hat — vor der ersten Wahl und nach
  // dem Löschen. Hingen die Spalten unten daran, zeigten sie Stationen und Personen einer
  // Prüfung, die gar nicht gewählt (oder nicht mehr vorhanden) ist.
  const geladen = useAppStore((s) => s.exam);
  const exam = geladen && geladen.id === examId ? geladen : null;

  const pickExam = useAppStore((s) => s.selectExam);
  const pickStation = useAppStore((s) => s.selectStation);
  const pickExaminer = useAppStore((s) => s.selectExaminer);
  const pickActor = useAppStore((s) => s.selectActor);

  const examiner = exam?.examiners.find((p) => p.id === examinerId) ?? null;
  const actor = exam?.actors.find((p) => p.id === actorId) ?? null;

  // Ob die dritte Spalte erscheint, entscheiden die Prüfung (`domain/schauspieler.ts`) und
  // seit 2026-10-02 die Einstellung `actors_enabled`.
  const schauspielerAn = useAppStore((s) => s.bogenAnzeige.schauspieler);
  const mitSchauspielern = schauspielerAn && fuehrtSchauspieler(exam);

  const blockCount = mitSchauspielern ? 3 : 2;

  /**
   * Im Startablauf ist dies der erste Schritt und kein Nachschlagen: dann schliesst das Blatt
   * nicht, sondern führt weiter zur Prüflingswahl — und nur, wenn Prüfung, Station und Prüfer
   * stehen. Der Schauspieler ist immer freiwillig.
   *
   * Später, aus der Kopfzeile geöffnet, verhält es sich wie bisher.
   */
  const imStart = startup.kind === 'auswahl';
  // Im Startablauf ist noch nichts gewählt — den Namen der zufällig geladenen Prüfung als
  // Titel zu zeigen, behauptete eine Wahl, die es nicht gibt.
  const titel = exam ? exam.name : t.setup.chooseTitle;
  const qr = useQrEinlesen();
  const vollständig = !!examId && !!station && !!examinerId;

  return (
    <Sheet
      kicker={t.setup.kicker}
      kickerIcon="exam"
      title={titel}
      onClose={imStart ? () => undefined : closeSheet}
      /* Der QR-Weg steht **in der Mitte** der Kopfzeile und trägt seine Beschriftung
         (Vorgabe des Nutzers, 2026-09-14). Er gehört nicht zu den Aktionen am Rand: die
         bestätigen oder schliessen, er beantwortet die Wahl auf einem anderen Weg — und mit
         einem vollständigen Code sogar das ganze Blatt (`useQrEinlesen`).

         Derselbe Scanner liest beides — den Einrichtungscode des Servers (`settings-…`) und
         einen Auswahlcode (`auswahl-…`). Was es war, entscheidet `domain/qrNutzlast.ts`,
         nicht dieser Knopf; deshalb nennt die Beschriftung beide. */
      mitte={
        <SheetAction
          icon="qr"
          label={t.qr.scanLang}
          onClick={() => void qr.scannen()}
          variant="outline"
        />
      }
      actions={
        <>
          {/* Der Weg in die Einstellungen — und im Startablauf der **einzige**: dort liegt
              hinter dem Blatt kein Bogen und damit auch nicht dessen Kopfzeile mit ihrem
              Zahnrad. Ohne ihn wäre ein Gerät ohne Prüfung eine Sackgasse: keine Prüfung,
              kein Bogen, keine Kopfzeile, kein Zugang zu Serveradresse und Demo-Knopf. */}
          <SheetAction
            icon="settings"
            label={t.header.settings}
            onClick={() => openSheet({ kind: 'settings' })}
            iconOnly
          />
          {/* Der Weg nach draussen — **nur am Rechner**, und hier aus demselben Grund wie das
              Zahnrad: in dieser Phase gibt es keine Kopfzeile, die ihn sonst trüge. Genau
              dieser Bildschirm stand beim Nutzer im Vollbild, als der Ausweg fehlte
              (2026-09-23). Er beendet nicht, er fragt — s. `features/beenden/`. */}
          {kannBeenden() && (
            <SheetAction
              icon="logout"
              label={t.exit.button}
              onClick={() => setBeendenOffen(true)}
              iconOnly
            />
          )}
          <SheetAction
            icon="check"
            /*
             * ⚠️ **Kurz, und in beiden Zuständen gleich** (Vorgabe des Nutzers, 2026-09-14).
             *
             * Bis dahin stand im gesperrten Zustand der ganze Satz „Prüfung, Station und
             * Prüfer wählen" im Knopf. Er drückte die Kopfzeile so weit auseinander, dass der
             * QR-Knopf in der Mitte in eine zweite Zeile rutschte. Was fehlt, sieht der Prüfer
             * ohnehin an den drei leeren Blöcken darunter; der Knopf sagt, wohin es geht.
             */
            label={imStart ? t.start.startStation : t.setup.apply}
            onClick={imStart ? confirmSetup : closeSheet}
            variant="primary"
            disabled={imStart && !vollständig}
          />
          {/* Im Startablauf gibt es nichts zu schliessen: dahinter liegt kein Bogen, und
              „Abbrechen" führte nur in denselben Dialog zurück — ein Knopf, der aussieht,
              als täte er etwas, und nichts tut. Später, aus der Kopfzeile geöffnet, ist das
              Kreuz der Weg zurück zum Bogen. */}
          {!imStart && (
            <SheetAction icon="close" label={t.setup.close} onClick={closeSheet} iconOnly />
          )}
        </>
      }
    >
      <div className={styles.body}>
        <div className={styles.card}>
          <div className={styles.cardHead}>
            <span className={styles.cardIcon}>
              <Icon name="doc" size={17} />
            </span>
            {/* Eine Überschrift für die Liste, **nicht** noch einmal der Prüfungsname: der
                steht schon im Titel des Blattes darüber und ein drittes Mal in der gewählten
                Zeile der Liste darunter (Befund des Nutzers, 2026-09-13). */}
            <div className={styles.cardText}>
              <div className={styles.cardValue}>{t.setup.pickTitle}</div>
            </div>
            {/* Nur im Classic-Modus: dort filtert der Server bei `current=true` auf ein
                Zeitfenster um heute und zeigt sonst nur einen Bruchteil des Bestands (am Server
                des Nutzers 2 von 19). Relay kennt keinen solchen Filter — der Schalter hätte
                dort nichts zu schalten und stünde nur im Weg. */}
            {katalog.modus === 'classic' && (
              <div className={styles.showAll}>
                <label className={styles.showAllLabel} htmlFor={alleId}>
                  {t.setup.showAll}
                </label>
                <Switch
                  id={alleId}
                  on={katalog.alleAnzeigen}
                  disabled={katalog.läuft}
                  onChange={(an) => {
                    katalog.setAlleAnzeigen(an);
                    void katalog.aktualisieren(an);
                  }}
                />
              </div>
            )}
            <button
              type="button"
              className={`tap ${styles.smallButton}`}
              data-busy={katalog.läuft}
              disabled={katalog.läuft}
              onClick={() => void katalog.aktualisieren()}
            >
              <Icon name="refresh" size={15} />
              {/*
                * ⚠️ **Die Beschriftung wechselt nicht** (Vorgabe des Nutzers, 2026-09-14).
                *
                * Bis dahin standen hier zwei Wörter gestapelt (`Aktualisieren` /
                * `Aktualisieren…`), damit der Knopf beim Takt nicht seine Breite ändert. Die
                * Breite blieb auch — der Inhalt des Knopfes ist aber zentriert, und so rutschte
                * das kürzere Wort bei jedem Schlag ein Stück zur Mitte. Genau dieses Wackeln
                * alle zehn Sekunden hat der Nutzer gemeldet.
                *
                * Dass gerade gefragt wird, sagt das **Symbol**: es dreht sich, solange `läuft`
                * gilt (`.smallButton[data-busy='true']`). Ein Zeichen, das sich an Ort und
                * Stelle dreht, bewegt nichts um sich herum.
                */}
              <span>{t.setup.refresh}</span>
            </button>
          </div>

          {/*
            * ⚠️ **Kein Server ist keine Meldung in Rot.** Ein Gerät ohne Server arbeitet
            * allein — das ist eine Auskunft und kein Vorfall (Vorgabe des Nutzers, s.
            * `keine-fehler-im-normalbetrieb`). Rot bleibt dem vorbehalten, was wirklich
            * schiefging: ein Abruf, der scheiterte, oder ein Download, der nicht durchkam.
            */}
          {katalog.fehler?.art === 'keinServer' && (
            <div className={styles.examNote}>{t.server.none}</div>
          )}

          {katalog.fehler && katalog.fehler.art !== 'keinServer' && (
            <div className={styles.examError}>
              {katalog.fehler.art === 'download' ? t.setup.downloadFailed : t.setup.refreshFailed}
              {' '}
              <span className={styles.examErrorDetail}>{katalog.fehler.text}</span>
            </div>
          )}

          <div className={`sc ${styles.examList}`}>
            {katalog.liste.map((e) => {
              const gewählt = e.id === examId;
              const vorhanden = e.presence !== 'nurServer';
              const arbeitet = beschäftigt === e.id;
              // Ein Problem und welches — mehr als eines kann eine Zeile nicht haben: `veraltet`
              // setzt voraus, dass der Server sie kennt, `nurLokal` das Gegenteil.
              const warnung = e.presence === 'nurLokal' ? t.setup.warnGone : e.veraltet ? t.setup.warnStale : null;
              const eigene = Object.entries(e.einstellungen ?? {});
              const einstellungenOffen = einstellungenVon === e.id && eigene.length > 0;
              return (
                /* ⚠️ `data-anhang` statt `:has()`: das gibt es erst ab Safari 15.4, und wir
                   zielen auf iOS 15.0. Es sagt der Zeile, dass unter ihr noch etwas kommt —
                   dann gibt sie ihre unteren Ecken ab, und Zeile und Streifen werden eine
                   Karte. */
                <div
                  key={e.id}
                  className={styles.examEntry}
                  data-anhang={Boolean(e.simulativ || warnung || einstellungenOffen)}
                >
                <div className={styles.examRow} data-on={gewählt} data-presence={e.presence}>
                  {/* Die Zeile selbst wählt aus — aber nur, was auch auf dem Gerät liegt.
                      Die Marke links ist dieselbe wie bei Station, Prüfer und Schauspieler
                      unten: Häkchen für gewählt, Punkt für nicht gewählt. */}
                  <button
                    type="button"
                    className={`tap ${styles.examPick}`}
                    disabled={!vorhanden || arbeitet}
                    onClick={() => pickExam(e.id)}
                  >
                    <span className={styles.examMark}>
                      <Icon name={gewählt ? 'check' : 'dot'} size={13} />
                    </span>
                    <span className={styles.examText}>
                      <span className={styles.examName}>{e.name}</span>
                      <span className={styles.examMeta}>{e.meta}</span>
                    </span>
                  </button>

                  <span className={styles.examState} data-presence={e.presence}>
                    {e.presence === 'nurLokal' && <Icon name="warning" size={14} />}
                    <span>
                      {e.presence === 'beide' && t.setup.presenceLoaded}
                      {e.presence === 'nurServer' && t.setup.presenceServer}
                      {e.presence === 'nurLokal' && t.setup.presenceLocal}
                    </span>
                  </span>

                  {/* Nur holen, was noch nicht da ist.
                      ⚠️ **Kein „erneut laden" mehr** (Entscheidung des Nutzers, 2026-09-13).
                      Der Knopf sah harmlos aus, war es aber nicht: er ersetzte den Inhalt
                      einer Prüfung, an der schon bewertet wurde, ohne dass jemand danach
                      gefragt hätte. Eine am Server berichtigte Prüfung meldet sich jetzt als
                      Warnung; wer sie übernehmen will, löscht sie und holt sie neu — und
                      sieht dabei im Löschdialog, welche Bewertungen das kostet. */}
                  {e.presence === 'nurServer' && (
                    <button
                      type="button"
                      className={`tap ${styles.examAction}`}
                      data-kind="download"
                      disabled={arbeitet}
                      aria-label={t.setup.download}
                      title={t.setup.download}
                      onClick={() => (e.geschuetzt ? setPasswortZiel(e) : void herunterladen(e.id))}
                    >
                      <Icon name="cloudDownload" size={18} />
                    </button>
                  )}
                  {/* Die Prüfung stellt etwas für sich ein (Relay `settings`): ein Tipp zeigt, was. */}
                  {eigene.length > 0 && (
                    <button
                      type="button"
                      className={`tap ${styles.examAction}`}
                      data-kind="einstellungen"
                      aria-expanded={einstellungenOffen}
                      aria-label={t.setup.pruefungsEinstellungen}
                      title={t.setup.pruefungsEinstellungen}
                      onClick={() => setEinstellungenVon(einstellungenOffen ? null : e.id)}
                    >
                      <Icon name="settings" size={17} />
                    </button>
                  )}
                  {vorhanden &&
                    (() => {
                      // Zweimal tippen: der erste Tipp schaltet nur scharf. Eine frisch
                      // geladene Prüfung hat keine Protokollzeile und wird von
                      // `DeleteExamDialog` bewusst ohne Rückfrage gelöscht — ohne diese
                      // Sperre wäre sie bei einem Fehlgriff sofort weg.
                      const istScharf = scharf?.id === e.id;
                      return (
                        <button
                          type="button"
                          className={`tap ${styles.examAction}`}
                          data-kind="delete"
                          data-scharf={istScharf}
                          style={{ '--loeschFrist': `${LOESCH_FRIST_MS}ms` } as CSSProperties}
                          disabled={arbeitet}
                          aria-label={istScharf ? t.setup.deleteArm : t.setup.deleteExam}
                          title={istScharf ? t.setup.deleteArm : t.setup.deleteExam}
                          onClick={() => papierkorbTippen(e)}
                        >
                          <Icon name={istScharf ? 'warning' : 'trash'} size={17} />
                        </button>
                      );
                    })()}
                </div>
                {/* Der Warnstreifen steht **unter** der Zeile, nicht darin: er trägt einen
                    ganzen Satz, und in der Zeile wäre er zwischen Name und Knöpfen zerdrückt
                    worden. */}
                {/* ⛔ **Die Simulationsprüfung steht über der Warnung, nicht an ihrer Stelle.**
                    Beides kann gleichzeitig zutreffen — eine Simulationsprüfung darf am Server
                    genauso geändert worden sein —, und `warnung` trägt nur einen Fall. Sie ist
                    ausserdem keine Warnung über einen Fehler, sondern eine Auskunft über die
                    Art der Prüfung: eigene Farbe, damit der Prüfer beides unterscheidet. */}
                {/* Eine Preview ist immer auch simulativ; ihr Satz sagt beides und ersetzt den
                    der Simulationsprüfung, statt einen zweiten Streifen darunter zu legen. */}
                {e.simulativ && (
                  <div className={styles.examSimulativ}>
                    <Icon name="info" size={14} />
                    <span>
                      {e.vorschau ? (
                        <>
                          <strong>{t.setup.vorschau}</strong> — {t.setup.vorschauNote}
                        </>
                      ) : (
                        <>
                          <strong>{t.setup.simulativ}</strong> — {t.setup.simulativNote}
                        </>
                      )}
                    </span>
                  </div>
                )}
                {einstellungenOffen && (
                  <div className={styles.examEinstellungen}>
                    <span>{t.setup.pruefungsEinstellungenNote}</span>
                    <ul>
                      {eigene.map(([name, roh]) => (
                        <li key={name}>
                          {eintragsText(name, locale)}: <strong>{zeigeEinstellung(name, roh)}</strong>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {warnung && (
                  <div className={styles.examWarn}>
                    <Icon name="warning" size={14} />
                    <span>{warnung}</span>
                  </div>
                )}
                </div>
              );
            })}
          </div>
        </div>

        <div
          className={styles.blocks}
          style={{ '--block-count': blockCount } as CSSProperties}
        >
          <Block
            tone="station"
            kicker={t.setup.station}
            icon="pin"
            value={station?.label ?? '—'}
            countLabel={t.setup.stations(exam?.stations.length ?? 0)}
            options={(exam?.stations ?? []).map((s) => ({
              id: s.id,
              label: s.label,
              meta: s.meta,
              on: s.id === station?.id,
            }))}
            onPick={(id) => void pickStation(id)}
          />
          <Block
            tone="examiner"
            kicker={t.setup.examiner}
            icon="user"
            value={examiner?.name ?? '—'}
            countLabel={t.setup.examiners(exam?.examiners.length ?? 0)}
            options={people(exam?.examiners ?? [], examiner ?? undefined)}
            onPick={pickExaminer}
          />
          {mitSchauspielern && (
            <Block
              tone="actor"
              kicker={t.setup.actor}
              icon="users"
              value={actor?.name ?? '—'}
              countLabel={t.setup.actors(exam?.actors.length ?? 0)}
              options={people(exam?.actors ?? [], actor ?? undefined)}
              onPick={pickActor}
            />
          )}
        </div>
      </div>
      {qr.vorschau && (
        <QrUebernahmeDialog
          uebernommen={qr.vorschau.uebernommen}
          ignoriert={qr.vorschau.ignoriert}
          gruppe={qr.vorschau.gruppe}
          onUebernehmen={() => void qr.uebernehmen()}
          onClose={qr.verwerfen}
        />
      )}
      {passwortZiel && (
        <PruefungsPasswortDialog
          pruefung={passwortZiel.name}
          zweck="laden"
          onClose={() => setPasswortZiel(null)}
          pruefe={async (eingabe) => {
            const ziel = passwortZiel;
            // Classic: die alte App verglich den Klartext der Liste am Gerät.
            if (ziel.klartextPasswort !== undefined && eingabe !== ziel.klartextPasswort) return false;
            setPasswortZiel(null);
            const angenommen = await herunterladen(ziel.id, eingabe);
            if (!angenommen) setPasswortZiel(ziel);
            return angenommen;
          }}
        />
      )}
      {löschSperre && (
        <PruefungsPasswortDialog
          pruefung={löschSperre.ziel.name}
          zweck="loeschen"
          onClose={() => setLöschSperre(null)}
          pruefe={async (eingabe) => {
            if (!(await pruefePasswort(löschSperre.ablage, eingabe))) return false;
            setLöschZiel(löschSperre.ziel);
            setLöschSperre(null);
            return true;
          }}
        />
      )}
      {löschZiel && (
        <DeleteExamDialog
          ziel={löschZiel}
          onClose={() => setLöschZiel(null)}
          onDeleted={() => {
            // Erst den Store aufräumen, dann die Liste neu lesen: sonst stünden unten für
            // einen Wimpernschlag noch die Stationen der gelöschten Prüfung.
            forgetExam(löschZiel.id);
            setLöschZiel(null);
            void katalog.lokalLesen();
          }}
        />
      )}
    </Sheet>
  );
}

interface Option {
  id: string;
  label: string;
  /** Die Beizeile rechts. Fehlt sie, steht dort nichts — s. `people`. */
  meta?: string;
  on: boolean;
}

/**
 * Prüfer und Schauspieler als Auswahl.
 *
 * ⚠️ **Ohne Beizeile.** Dort stand `Person.meta`, also `groupName`. Eine Gruppe gibt es bei
 * Prüfern und Schauspielern nicht (Nutzer, 2026-09-13): am Classic-Server tragen alle 92
 * Einträge der 18 Prüfungen wörtlich den Wert `"Group"` — ein Platzhalter des Classic-Servers, der
 * neben jedem Namen stand und nichts aussagte.
 *
 * Das Feld selbst bleibt in der Ablage: `Examiner.groupName` und `Actor.groupName` gehören
 * zum Relay-Schema (`memory/relay-schema-is-source.md`), und der Download soll nichts
 * wegwerfen, was dort steht.
 */
const people = (list: Person[], current: Person | undefined): Option[] =>
  list.map((p) => ({ id: p.id, label: p.name, on: p.id === current?.id }));

function Block({
  tone,
  kicker,
  icon,
  value,
  countLabel,
  options,
  onPick,
}: {
  tone: 'station' | 'examiner' | 'actor';
  kicker: string;
  icon: IconName;
  value: string;
  countLabel: string;
  options: Option[];
  onPick: (id: string) => void;
}) {
  return (
    <div className={styles.block} data-tone={tone}>
      <div className={styles.blockHead}>
        <span className={styles.blockIcon}>
          <Icon name={icon} size={17} />
        </span>
        <div className={styles.blockText}>
          <div className={styles.blockKicker}>{kicker}</div>
          <div className={styles.blockValue}>{value}</div>
        </div>
        <span className={styles.blockCount}>{countLabel}</span>
      </div>
      <div className={`sc ${styles.blockList}`}>
        {options.map((o) => (
          <button
            key={o.id}
            type="button"
            className={`tap ${styles.option}`}
            data-on={o.on}
            onClick={() => onPick(o.id)}
          >
            <span className={styles.optionMark}>
              <Icon name={o.on ? 'check' : 'dot'} size={13} />
            </span>
            <span className={styles.optionLabel}>{o.label}</span>
            {/* Nur wenn es etwas zu sagen gibt — ein leeres Feld nähme dem Namen Platz weg. */}
            {o.meta ? <span className={styles.optionMeta}>{o.meta}</span> : null}
          </button>
        ))}
      </div>
    </div>
  );
}
